import React,{ useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Chip } from "primereact/chip";

import { Table, DropDownField, MultiSelectField } from "../../helpers";
import Loading from "../../common/Loading";

import { fetchPermActions } from "../../services/redux/reducers/permissions/permissions";

import "./index.css"

import profileImage from "../../images/company_user.png"
import exceptionImage from "../../images/exception_role.png"


import { FilterMatchMode } from "primereact/api"
import { FilterService } from 'primereact/api'
import { getFullName, sortArrayOfObjectsInAlphabeticalOrder } from "../../utils";

FilterService.register('custom_company_roles_match_mode', (rowWiseCompanyRoleList, selectedRoles) => {
    if (!selectedRoles || selectedRoles.length === 0){
        return true
    }
    return selectedRoles.some(eachRole => rowWiseCompanyRoleList.includes(eachRole))
})


const Permissions = () => {
    const [isLoading, setIsLoading] = useState(false)

    const [updatedPermissionsData, setUpdatedPermissionsData] = useState([])
    const [companyRolesOptions, setCompanyRolesOptions] = useState([])
    const [positiveExceptionUsers, setPositiveExceptionUsers] = useState([])
    const [negativeExceptionUsers, setNegativeExceptionUsers] = useState([])

    const [filters, setFilters] = useState({
        companyRoleNames: { value: null, matchMode: 'custom_company_roles_match_mode' },
        positiveExceptionUsers: { value: null, matchMode: FilterMatchMode.CONTAINS },
        negativeExceptionUsers: { value: null, matchMode: FilterMatchMode.CONTAINS }
    })

    const isTableLoading = useSelector(state => state.permissions.loading)

    const searchParams = useSelector(state => state.permissions.searchParams)
    const permissionsData = useSelector(state => state.permissions.permissionsData)

    const dispatch = useDispatch()
    
    useEffect(() => {
        const updatedSearchParams = {
            ...searchParams,
            paginator: false
        }
        dispatch(fetchPermActions(updatedSearchParams))
    }, [])

    useEffect(() => {
        const roleSet = new Set()
        const positiveUserSet = new Set()
        const negativeUserSet = new Set()
    
        permissionsData.forEach(eachPerm => {
            eachPerm.permExceptions.forEach(eachPermException => {
                const user = eachPermException.userRole.user
                if (user) {
                    const fullName = getFullName(user.firstName, user.lastName)
                    if (eachPermException.permExceptionGrant) {
                        positiveUserSet.add(fullName)
                    } else {
                        negativeUserSet.add(fullName)
                    }
                }
            })
        })
    
        let positiveOptions = Array.from(positiveUserSet).map(name => ({ label: name, value: name }))
        let negativeOptions = Array.from(negativeUserSet).map(name => ({ label: name, value: name }))

        positiveOptions = sortArrayOfObjectsInAlphabeticalOrder(positiveOptions, "label")
        negativeOptions = sortArrayOfObjectsInAlphabeticalOrder(negativeOptions, "label")
    
        setPositiveExceptionUsers(positiveOptions)
        setNegativeExceptionUsers(negativeOptions)

        const updatedPermissionsData = permissionsData.map(eachPermission => {
            const roleNames = eachPermission.permRoles.map(eachPermRole => eachPermRole.companyRole.companyRoleName)
            roleNames.forEach(eachRole => roleSet.add(eachRole))

            const positiveExceptionUsers = eachPermission.permExceptions.filter(eachException => eachException.permExceptionGrant).map(eachException => {
                const user = eachException.userRole.user
                return user ? getFullName(user.firstName, user.lastName) : null
            })

            const negativeExceptionUsers = eachPermission.permExceptions.filter(eachException => !eachException.permExceptionGrant).map(eachException => {
                const user = eachException.userRole.user
                return user ? getFullName(user.firstName, user.lastName) : null
            })

            return {
                ...eachPermission,
                companyRoleNames: roleNames,
                positiveExceptionUsers: positiveExceptionUsers,
                negativeExceptionUsers: negativeExceptionUsers
            }
        })

        setUpdatedPermissionsData(updatedPermissionsData)
        let companyRoleList = Array.from(roleSet).map(eachRole => ({ label: eachRole, value: eachRole }))
        companyRoleList = sortArrayOfObjectsInAlphabeticalOrder(companyRoleList, "label")
        setCompanyRolesOptions(companyRoleList)

    }, [permissionsData])

    const companyRolesFilterTemplate = (options) => (
        <MultiSelectField
            value={options.value}
            options={companyRolesOptions}
            onChange={(e) => options.filterApplyCallback(e.value)}
            placeholder="Filter by Roles"
            className="p-column-filter"
            style={{ width: '320px' }}
            maxSelectedLabels={0}
            selectedItemsLabel="{0} roles selected"
        />
    )

    const positiveExceptionFilterTemplate = (options) => (
        <DropDownField
            value={options.value}
            options={positiveExceptionUsers}
            onChange={(e) => options.filterApplyCallback(e.value)}
            placeholder="Filter by user"
            style={{ width: '250px' }}
        />
    )

    const negativeExceptionFilterTemplate = (options) => (
        <DropDownField
            value={options.value}
            options={negativeExceptionUsers}
            onChange={(e) => options.filterApplyCallback(e.value)}
            placeholder="Filter by user"
            style={{ width: '250px' }}
        />
    )

    const getCompanyRolesBody = (row) => {
        return (
            <div className="company-perm-roles">
                {row.permRoles?.map((eachRole, index) => {
                    const roleName = eachRole.companyRole?.companyRoleName 
                    return roleName ? (
                        <Chip
                            key={index}
                            label={roleName}
                            className="mr-1 mb-1"
                        />
                    ) : "-"
                })}
            </div>
        )
    }

    const getPositiveExceptionsBody = (row) => {
        const positiveExceptions = row.permExceptions.filter(eachException => eachException.permExceptionGrant === true)

        return positiveExceptions.length > 0 ?
            (
                <div className="perm-exceptions">
                    {positiveExceptions.map((eachException, index) => {
                        const user = eachException.userRole.user
                        const userName = user ? getFullName(user.firstName, user.lastName) : "-"

                        return userName ? (
                            <Chip
                                key={index}
                                label={userName}
                                image={exceptionImage}
                                className="mr-2 mb-2"
                                style={{backgroundColor: "lightgreen"}}
                            />
                        ) : null
                    })}
                </div>
            )
            : "-"
    }

    const getNegativeExceptionsBody = (row) => {
        const negativeExceptions = row.permExceptions.filter(eachException => !eachException.permExceptionGrant)

        return negativeExceptions.length > 0 ?
            (
                <div className="perm-exceptions">
                    {negativeExceptions.map((eachException, index) => {
                        const user = eachException.userRole.user
                        const userName = user ? getFullName(user.firstName, user.lastName) : "-"

                        return userName ? (
                            <Chip
                                key={index}
                                label={userName}
                                image={exceptionImage}
                                className="mr-2 mb-2"
                                style={{backgroundColor: "#ffc3bd"}}
                            />
                        ) : null
                    })}
                </div>
            )
            : "-"
    }

    const tableColumns = [
        {
            header: 'Action ID', field: 'permActionId', style:{ textAlign: "center", minWidth: "80px" }
        },
        {
            header: 'Category', field: 'permActionCategory', style:{ textAlign: "center", minWidth: "120px" }
        },
        {
            header: 'LongName', field: 'permActionLongName', style:{ textAlign: "center", minWidth: "200px" }
        },
        {
            header: 'ShortName', field: 'permActionShortName', style:{ textAlign: "center", minWidth: "120px" }
        },
        {
            header: 'Description', field: 'permActionDesc', style:{ textAlign: "center", minWidth: "220px" }
        },
        {
            header: 'Company Roles',
            field: 'companyRoleNames',
            body: getCompanyRolesBody,
            filter: true,
            filterField: 'companyRoleNames',
            filterElement: companyRolesFilterTemplate,
            style: { textAlign: "center", width: '320px' }
        },
        {
            header: 'Positive Exceptions',
            body: getPositiveExceptionsBody,
            filter: true,
            filterField: 'positiveExceptionUsers',
            filterElement: positiveExceptionFilterTemplate,
            style: { width: "250px", textAlign: "center" }
        },
        {
            header: 'Negative Exceptions',
            body: getNegativeExceptionsBody,
            filter: true,
            filterField: 'negativeExceptionUsers',
            filterElement: negativeExceptionFilterTemplate,
            style: { width: "250px", textAlign: "center" }
        }
    ]

    return (
        <div>
            <div className="vp-cheque-layout flex flex-column">
                <div className="flex flex-column md:flex-row md:justify-content-between">
                    <h4 className="mx-2 my-1 md:m-2">PERMISSIONS</h4>
                </div>

                <div className='flex flex-row'>
                    <div className="vp-cheque-table" style={{ order: "1", width: "100%", maxHeight: "'calc(100%-100px)'" }}>
                        <div className='p-card mb-0' >
                            <div className="flex-grow-1" >
                                <Table
                                    tableColumns={tableColumns}
                                    data={updatedPermissionsData}
                                    filters={filters}
                                    onFilter={(e) => setFilters(e.filters)}
                                    filterDisplay="row"
                                    scrollable={"true"}
                                    tableLayout="fixed"
                                    // scrollHeight="600px"
                                    loading={isTableLoading}
                                />
                            </div>
                        </div>
                    </div>

                    <Loading visible={isLoading}/>
                </div>
            </div>
        </div>
    )
}

export default Permissions;
